import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import { useAuth } from '@/hooks/useAuth';
import { LoadingSpinner } from '@/presentation/components/LoadingSpinner';
import { RequireAuth } from '@/routes/RequireAuth';

// Lazy load components for better performance
const LandingPage = React.lazy(() => import('@/screens/Landing'));
const HomeLayout = React.lazy(() => import('@/screens/HomeLayout'));
const LoginPage = React.lazy(() => import('@/screens/Login'));
const RegisterPage = React.lazy(() => import('@/screens/Register'));
const DashboardPage = React.lazy(() => import('@/screens/Dashboard'));
const SettingsPage = React.lazy(() => import('@/screens/Settings'));
const UsersPage = React.lazy(() => import('@/screens/Users'));
const NotFoundPage = React.lazy(() => import('@/screens/NotFound'));

// Blog pages
const BlogPage = React.lazy(() => import('@/screens/Blog'));
const BlogPostText = React.lazy(() => import('@/screens/Blog/BlogPages/Text'));
const BlogPostVideo = React.lazy(
  () => import('@/screens/Blog/BlogPages/Video')
);

// Product pages
const ProductPage = React.lazy(() => import('@/screens/Produtos'));

// Institutional pages
const PoliticaPage = React.lazy(() => import('@/screens/PoliticaPrivacidade'));
const TermosPage = React.lazy(() => import('@/screens/TermosUso'));

// Payment flow pages (moved to PagarBranded)
const PaymentFlowPage = React.lazy(
  () => import('@/screens/PagarBranded/pages/PaymentFlowPage')
);
const PaymentStatusPage = React.lazy(
  () => import('@/screens/PagarBranded/pages/PaymentStatusPage')
);
// Removed unused PaymentReceiptPage import

// Note: PaymentLogsPage, PaymentReceipt, and PaymentProfilePage were removed
const LogoutPage = React.lazy(
  () => import('@/screens/PagarBranded/pages/LogoutPage')
);

// Public route component (redirects if already authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <LoadingSpinner size='lg' />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to='/dashboard' replace />;
  }

  return <>{children}</>;
};

export const AppRoutes: React.FC = () => {
  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingSpinner size='lg' />}>
        <Routes>
          {/* Protected routes */}
          <Route element={<RequireAuth />}>
            <Route path='/dashboard' element={<DashboardPage />} />
            <Route path='/settings' element={<SettingsPage />} />
            <Route path='/users' element={<UsersPage />} />
            {/* Removed: payment-logs, payment-receipt, and payment-profile routes no longer exist */}
          </Route>

          {/* Public routes */}
          <Route path='/' index element={<LandingPage />} />
          <Route path='/classic' element={<HomeLayout />} />
          <Route
            path='/login'
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route path='/register' element={<RegisterPage />} />

          {/* Blog routes */}
          <Route path='/blog' element={<BlogPage />} />
          <Route path='/blog/post/:id' element={<BlogPostText />} />
          <Route path='/blog/video/:id' element={<BlogPostVideo />} />

          {/* Product routes */}
          <Route path='/produtos/:productId' element={<ProductPage />} />

          {/* Institutional pages */}
          <Route path='/politica-privacidade' element={<PoliticaPage />} />
          <Route path='/termos-uso' element={<TermosPage />} />

          {/* Institutional pages redirects - for backward compatibility */}
          <Route
            path='/sobre'
            element={<Navigate to='/#lideranca-section' replace />}
          />
          <Route
            path='/reconhecimentos'
            element={<Navigate to='/#premios-section' replace />}
          />
          <Route
            path='/faq'
            element={<Navigate to='/#central-ajuda' replace />}
          />

          {/* Payment flow routes */}
          <Route
            path='/:userTag/:paymentId/status'
            element={<PaymentStatusPage />}
          />
          <Route path='/:userTag/:paymentId' element={<PaymentFlowPage />} />
          <Route path='/:userTag/sair' element={<LogoutPage />} />
          <Route path='/:userTag/login' element={<PaymentFlowPage />} />
          <Route path='/:userTag' element={<PaymentFlowPage />} />

          {/* Catch all route */}
          <Route path='*' element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};
